// =============================================================================
// components/ErrorBoundary.tsx — root render-crash net.
//
// Triage P0 (IMG_5857): a render throw anywhere in the tree produced a BLANK
// WHITE screen in release (no red-box). This class boundary catches render/
// lifecycle errors and shows a branded glass recovery screen with a "Try again"
// that remounts the subtree (children get a fresh key), instead of a dead app.
// Mounted at the App root around the NavigationContainer.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { colors, spacing, radius, fonts } from '../constants/theme';
import { captureError } from '../lib/crashReporter';

interface Props { children: React.ReactNode }
interface State { error: Error | null; stack: string | null; resetKey: number }

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, stack: null, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log for device console capture. A crash reporter IS now wired (DSN-gated,
    // dependency-safe — lib/crashReporter.ts): captureError is a clean no-op
    // until EXPO_PUBLIC_SENTRY_DSN is set + @sentry/react-native installed/linked,
    // and never throws — so behavior below is unchanged (still logs, still renders
    // the fallback).
    console.error('[CRYB] Root render error:', error?.message, info?.componentStack?.slice(0, 400));
    try {
      captureError(error, { source: 'ErrorBoundary', componentStack: info?.componentStack?.slice(0, 2000) });
    } catch {}
    // Surface the diagnostic on-screen too (selectable) so a release/TestFlight
    // crash can be read without a Mac console attached.
    this.setState({ stack: info?.componentStack ?? null });
  }

  private reset = () => {
    this.setState((s) => ({ error: null, resetKey: s.resetKey + 1 }));
  };

  render() {
    if (this.state.error) {
      return (
        <View style={styles.container}>
          <View style={styles.card}>
            <Text style={styles.logo}>CRYB</Text>
            <Text style={styles.title}>Something went wrong</Text>
            <Text style={styles.sub}>
              An unexpected error interrupted the app. Tap below to recover — your account is safe.
            </Text>
            {/* Compact one-line error message, shown in release too — a single
                message (e.g. "undefined is not a function") is not a scary raw
                stack but lets us diagnose a TestFlight crash from a screenshot.
                The full component stack stays DEV-only below. */}
            {!!this.state.error?.message && (
              <Text selectable style={styles.errLine} numberOfLines={3}>
                {String(this.state.error.message).slice(0, 200)}
              </Text>
            )}
            {/* DEV-only diagnostic: the full component stack. */}
            {__DEV__ && (
              <ScrollView style={styles.diag} contentContainerStyle={styles.diagContent}>
                <Text selectable style={styles.diagText}>
                  {String(this.state.error?.message || this.state.error || 'unknown error')}
                  {'\n\n'}
                  {String(this.state.stack || '').slice(0, 1200)}
                </Text>
              </ScrollView>
            )}
            <TouchableOpacity style={styles.button} onPress={this.reset} accessibilityRole="button">
              <Text style={styles.buttonText}>Try again</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }
    // Fresh key on reset → the whole tree (navigation included) remounts clean.
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#E8E1F2',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxl,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: 'rgba(255,255,255,0.75)',
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.9)',
    padding: spacing.xxl,
    alignItems: 'center',
  },
  diag: {
    maxHeight: 220,
    alignSelf: 'stretch',
    backgroundColor: 'rgba(0,0,0,0.06)',
    borderRadius: radius.md,
    marginBottom: spacing.lg,
  },
  diagContent: { padding: spacing.md },
  diagText: { fontSize: 11, color: '#B00020', lineHeight: 15 },
  errLine: {
    fontSize: 12,
    color: '#B00020',
    textAlign: 'center',
    marginBottom: spacing.lg,
    paddingHorizontal: spacing.sm,
  },
  logo: { fontSize: 22, letterSpacing: 2, color: colors.primary, ...fonts.extrabold, marginBottom: spacing.lg },
  title: { fontSize: 18, color: colors.ink, ...fonts.semibold, marginBottom: spacing.sm },
  sub: { fontSize: 13.5, color: colors.muted, textAlign: 'center', lineHeight: 19, marginBottom: spacing.xl },
  button: {
    backgroundColor: colors.primary,
    borderRadius: radius.md,
    paddingVertical: 12,
    paddingHorizontal: spacing.xxl,
  },
  buttonText: { color: colors.white, fontSize: 15, ...fonts.semibold },
});
